/**
 * **The revenue sheet's logic, as pure functions — RV-5.**
 *
 * `components/RevenueSheet.tsx` is the view; everything a shortcut *does* is
 * here, so every one of them is testable without a browser. The keyboard
 * contract follows `dataceva-llc/reporting-platform-blueprint`'s DataGrid
 * (`useGridNavigation`), which was built for exactly this: somebody entering a
 * month of figures in one sitting.
 *
 * Coordinates are `{ row, col }`, rows top-to-bottom as dates and columns
 * left-to-right as channels. Values are the grid's own strings — `''` is an
 * empty cell, which saves as *not recorded*, never zero.
 */
import { parsePaste } from './gridPaste'
import { sanitizeMoneyInput } from './money'

export interface Cell {
  row: number
  col: number
}

export interface Rect {
  top: number
  left: number
  bottom: number
  right: number
}

export interface Shape {
  rows: number
  cols: number
}

/** The server's save cap (`BatchWrite.cells`, `api/revenue.py`), kept (RV-7). */
export const SAVE_CAP = 2000

export const PAGE_ROWS = 10

export function rectOf(anchor: Cell, active: Cell): Rect {
  return {
    top: Math.min(anchor.row, active.row),
    bottom: Math.max(anchor.row, active.row),
    left: Math.min(anchor.col, active.col),
    right: Math.max(anchor.col, active.col),
  }
}

export function inRect(rect: Rect, row: number, col: number): boolean {
  return row >= rect.top && row <= rect.bottom && col >= rect.left && col <= rect.right
}

export function rectSize(rect: Rect): number {
  return (rect.bottom - rect.top + 1) * (rect.right - rect.left + 1)
}

function clamp(cell: Cell, shape: Shape): Cell {
  return {
    row: Math.max(0, Math.min(shape.rows - 1, cell.row)),
    col: Math.max(0, Math.min(shape.cols - 1, cell.col)),
  }
}

export type Move =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'pageUp'
  | 'pageDown'
  | 'rowStart'
  | 'rowEnd'
  | 'gridStart'
  | 'gridEnd'
  | 'edgeUp'
  | 'edgeDown'
  | 'edgeLeft'
  | 'edgeRight'

/** Where a navigation key lands, clamped to the grid. */
export function move(from: Cell, how: Move, shape: Shape): Cell {
  const last = { row: shape.rows - 1, col: shape.cols - 1 }
  const next: Record<Move, Cell> = {
    up: { row: from.row - 1, col: from.col },
    down: { row: from.row + 1, col: from.col },
    left: { row: from.row, col: from.col - 1 },
    right: { row: from.row, col: from.col + 1 },
    pageUp: { row: from.row - PAGE_ROWS, col: from.col },
    pageDown: { row: from.row + PAGE_ROWS, col: from.col },
    rowStart: { row: from.row, col: 0 },
    rowEnd: { row: from.row, col: last.col },
    gridStart: { row: 0, col: 0 },
    gridEnd: last,
    edgeUp: { row: 0, col: from.col },
    edgeDown: { row: last.row, col: from.col },
    edgeLeft: { row: from.row, col: 0 },
    edgeRight: { row: from.row, col: last.col },
  }
  return clamp(next[how], shape)
}

/** Tab order: left to right, wrapping onto the next row. `null` past the end. */
export function tabFrom(from: Cell, backwards: boolean, shape: Shape): Cell | null {
  const index = from.row * shape.cols + from.col + (backwards ? -1 : 1)
  if (index < 0 || index >= shape.rows * shape.cols) return null
  return { row: Math.floor(index / shape.cols), col: index % shape.cols }
}

/** A value lookup, and the key the edits map uses for a cell. */
export interface Grid {
  shape: Shape
  valueAt: (row: number, col: number) => string
  keyAt: (row: number, col: number) => string
  /** False for a day after today (RV-7): shown, never writable. */
  writable: (row: number, col: number) => boolean
}

export type Edits = Record<string, string>

export interface Outcome {
  edits: Edits
  error?: string
}

function refuseOverCap(current: Edits, staged: Edits): string | undefined {
  const total = Object.keys({ ...current, ...staged }).length
  return total > SAVE_CAP
    ? `That Would Stage ${total.toLocaleString()} Changes, Over the ${SAVE_CAP.toLocaleString()} ` +
        `One Save Can Hold, so Nothing Was Changed. Save First, or Work in Smaller Blocks.`
    : undefined
}

function stage(grid: Grid, current: Edits, cells: [number, number, string][]): Outcome {
  const staged: Edits = {}
  let skipped = 0
  for (const [row, col, value] of cells) {
    if (!grid.writable(row, col)) {
      skipped += 1
      continue
    }
    staged[grid.keyAt(row, col)] = value
  }
  const error = refuseOverCap(current, staged)
  if (error) return { edits: {}, error }
  if (!Object.keys(staged).length && skipped) {
    return { edits: {}, error: 'Future Dates Cannot Be Entered.' }
  }
  return { edits: staged }
}

/** `Delete` / `Backspace` over a selection: every cell becomes empty. */
export function clear(grid: Grid, rect: Rect, current: Edits): Outcome {
  const cells: [number, number, string][] = []
  for (let row = rect.top; row <= rect.bottom; row += 1) {
    for (let col = rect.left; col <= rect.right; col += 1) cells.push([row, col, ''])
  }
  return stage(grid, current, cells)
}

/** `Ctrl+D`: the top row of the selection copied down through the rest. */
export function fillDown(grid: Grid, rect: Rect, current: Edits): Outcome {
  const cells: [number, number, string][] = []
  for (let col = rect.left; col <= rect.right; col += 1) {
    const source = grid.valueAt(rect.top, col)
    for (let row = rect.top + 1; row <= rect.bottom; row += 1) cells.push([row, col, source])
  }
  return stage(grid, current, cells)
}

/** `Ctrl+R`: the left column of the selection copied across. */
export function fillRight(grid: Grid, rect: Rect, current: Edits): Outcome {
  const cells: [number, number, string][] = []
  for (let row = rect.top; row <= rect.bottom; row += 1) {
    const source = grid.valueAt(row, rect.left)
    for (let col = rect.left + 1; col <= rect.right; col += 1) cells.push([row, col, source])
  }
  return stage(grid, current, cells)
}

/** `Ctrl+Enter` while editing: one value into every selected cell. */
export function fillSelection(grid: Grid, rect: Rect, value: string, current: Edits): Outcome {
  const cells: [number, number, string][] = []
  for (let row = rect.top; row <= rect.bottom; row += 1) {
    for (let col = rect.left; col <= rect.right; col += 1) cells.push([row, col, value])
  }
  return stage(grid, current, cells)
}

/**
 * `Ctrl+C`: the selection as tab-separated text, rows on lines — the shape
 * Google Sheets and Excel paste as a block, and the shape `parsePaste` reads.
 */
export function copyText(grid: Grid, rect: Rect): string {
  const lines: string[] = []
  for (let row = rect.top; row <= rect.bottom; row += 1) {
    const cells: string[] = []
    for (let col = rect.left; col <= rect.right; col += 1) cells.push(grid.valueAt(row, col))
    lines.push(cells.join('\t'))
  }
  return lines.join('\n')
}

/**
 * `Ctrl+V`. Pastes by position from the selection's top-left, through the
 * existing all-or-nothing parser: one unparseable value rejects the whole block.
 *
 * **One value over a larger selection fills every selected cell**, which is
 * what a spreadsheet does and how a column is set in one keystroke.
 */
export function paste(
  grid: Grid,
  rect: Rect,
  text: string,
  current: Edits,
  target: { profileIds: string[]; days: string[] },
): Outcome {
  const single = !/[\t\n]/.test(text.replace(/\r?\n$/, ''))
  if (single && rectSize(rect) > 1) {
    const raw = text.trim()
    const value = raw === '' ? '' : sanitizeMoneyInput(raw, '')
    if (raw !== '' && value === '') {
      return { edits: {}, error: `“${raw}” Is Not a Number, so Nothing Was Pasted.` }
    }
    return fillSelection(grid, rect, value, current)
  }
  const result = parsePaste(text, { ...target, anchor: { row: rect.top, col: rect.left } })
  if (result.error) return { edits: {}, error: result.error }
  // Re-staged through the same gate, so future rows and the save cap apply to a
  // paste exactly as they do to typing.
  const cells: [number, number, string][] = []
  const rowOf = new Map(target.days.map((day, index) => [day, index]))
  const colOf = new Map(target.profileIds.map((id, index) => [id, index]))
  for (const [key, value] of Object.entries(result.edits)) {
    const separator = key.indexOf('|')
    const row = rowOf.get(key.slice(separator + 1))
    const col = colOf.get(key.slice(0, separator))
    if (row !== undefined && col !== undefined) cells.push([row, col, value])
  }
  return stage(grid, current, cells)
}

/** The Sum · Average · Count bar under the sheet. Blanks add nothing, not zero. */
export function summarise(
  grid: Grid,
  rect: Rect,
  parse: (text: string) => number | null,
): { sum: number; average: number | null; count: number } {
  let sum = 0
  let count = 0
  for (let row = rect.top; row <= rect.bottom; row += 1) {
    for (let col = rect.left; col <= rect.right; col += 1) {
      const value = parse(grid.valueAt(row, col))
      if (value !== null) {
        sum += value
        count += 1
      }
    }
  }
  return { sum, average: count ? sum / count : null, count }
}

/**
 * **Undo covers unsaved edits only** — the blueprint's rule. Save and Discard
 * end the history; after a save, the change record is the history and a
 * correction is a new edit.
 */
export interface History {
  past: Edits[]
  future: Edits[]
}

export const HISTORY_LIMIT = 100

export function record(history: History, before: Edits): History {
  return { past: [...history.past, before].slice(-HISTORY_LIMIT), future: [] }
}

export function undo(history: History, present: Edits): { history: History; edits: Edits } | null {
  const previous = history.past.at(-1)
  if (previous === undefined) return null
  return {
    history: { past: history.past.slice(0, -1), future: [present, ...history.future] },
    edits: previous,
  }
}

export function redo(history: History, present: Edits): { history: History; edits: Edits } | null {
  const next = history.future[0]
  if (next === undefined) return null
  return {
    history: { past: [...history.past, present], future: history.future.slice(1) },
    edits: next,
  }
}

/**
 * Staging a value equal to what is stored is not an edit: it would count
 * against the save, tint the cell and write an audit row saying nothing
 * changed. This drops those keys.
 */
export function prune(edits: Edits, unchanged: (key: string, value: string) => boolean): Edits {
  return Object.fromEntries(
    Object.entries(edits).filter(([key, value]) => !unchanged(key, value)),
  )
}
